import { Upload } from "@mui/icons-material";
import CameraIcon from "@mui/icons-material/Camera";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import UploadMediaDialog from "./captureDialog";
import CustomCard from "./CustomCard";
import "./Home.css";
import { LocationContext } from "./LocationContext";
import MediaPreviewDialog from "./MediaDialog";

const convertDateToDDMMYYYY = (isoDate) => {
  if (isoDate) {
    const date = new Date(isoDate);

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }
  return "";
};

const transformDataForMediaPreview = (data) => {
  return data?.map((caseItem) => ({
    caseid: caseItem?.caseid,
    allImages: caseItem?.images.map((image) => ({
      url: image.fileUrl,
      type: "image",
    })),
    priority: caseItem?.jsonFiles?.result?.Priority || "",
    label: caseItem?.jsonFiles?.result?.label || "Open",
    reason: caseItem?.jsonFiles?.result?.reason || "",
    location: caseItem?.jsonFiles?.metadata?.state || "",
    department: caseItem?.jsonFiles?.result?.department || "",
    date: convertDateToDDMMYYYY(caseItem?.jsonFiles?.metadata?.date),
    description: caseItem?.jsonFiles?.metadata?.description,
    city: caseItem?.jsonFiles?.metadata?.city || "",
  }));
};

const Home = () => {
  const [opencaptureDialog, setOpenCaptureDialog] = useState(false);
  const [dialogMedia, setDialogMedia] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [caseData, setCaseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const cameraRef = useRef();
  const videoRef = useRef();
  const navigate = useNavigate();
  const userId = localStorage.getItem("username");
  const { location } = useContext(LocationContext);

  const fetchCaseData = async () => {
    const apiBaseUrl =
      process.env.REACT_APP_API_BASE_URL || "https://intelzen-1.onrender.com";

    try {
      setLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/api/cases-with-images-json/${userId}`
      );

      const modified = transformDataForMediaPreview(response.data.cases);
      const updated = modified?.sort(
        (a, b) => new Date(b?.caseid) - new Date(a?.caseid)
      );

      setCaseData(updated || []);
    } catch (error) {
      console.error("Error fetching case data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseData();
    const intervalId = setInterval(fetchCaseData, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const handleUpload = () => {
    setDialogMedia(null);
    setOpenCaptureDialog(true);
  };

  const handleCaptureInput = (event) => {
    const file = event.target.files[0];
    if (file) {
      setDialogMedia([file]);
      setOpenCaptureDialog(true);
    }
  };

  const disablestate = useMemo(() => {
    return ["texans"].includes(location?.state?.toLowerCase()) ? true : false;
  }, [location]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setImages([]);
  };

  const handleMediaClick = (images) => {
    setImages(images);
    setOpenDialog(true);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 2 }}>
      <Grid container spacing={2}>
        {disablestate && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  backgroundColor: "#f29797",
                  fontWeight: "bolder",
                }}
              >
                The application has not been released in the state{" "}
                {location?.state || "Unknown"}
              </Alert>
            </Box>
          </Grid>
        )}

        {/* TOP BUTTONS UI */}
        <Grid
          container
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
          sx={{ padding: "4px" }}
        >
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleUpload}
              disabled={disablestate}
              fullWidth
              sx={{
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Upload
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box>
              <Button
                variant="contained"
                disabled={disablestate}
                fullWidth
                startIcon={<CameraIcon />}
                color="primary"
                onClick={() => cameraRef.current.click()}
                sx={{
                  padding: "10px 20px",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "14px",
                  borderRadius: "8px",
                }}
              >
                Capture Image
              </Button>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/svg+xml, image/bmp, image/tiff"
                ref={cameraRef}
                capture="environment"
                onChange={handleCaptureInput}
                style={{ display: "none" }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box>
              <Button
                variant="contained"
                fullWidth
                disabled={disablestate}
                startIcon={<CameraIcon />}
                color="primary"
                onClick={() => videoRef.current.click()}
                sx={{
                  padding: "10px 20px",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "14px",
                  borderRadius: "8px",
                }}
              >
                Capture Video
              </Button>
              <input
                type="file"
                accept="video/*"
                ref={videoRef}
                capture="environment"
                onChange={handleCaptureInput}
                style={{ display: "none" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* LOADING SECTION */}
      {loading ? (
        <Box sx={{ textAlign: "center", marginTop: 4 }}>
          <CircularProgress />
          <Typography variant="h6">Loading cases...</Typography>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, padding: 2 }}>
          <Grid container spacing={2}>
            {caseData?.map((caseItem) => (
              <Grid item xs={12} sm={6} md={4} key={`${caseItem.caseid}`}>
                <CustomCard
                  item={{
                    caseid: caseItem.caseid,
                    openDialog: openDialog,
                    handleDialogClose: handleDialogClose,
                    handleMediaClick: handleMediaClick,
                    setOpenDialog: setOpenDialog,
                    category: caseItem.label,
                    priority: caseItem.priority,
                    date: caseItem.date,
                    description: caseItem.description,
                    location: caseItem?.location || "",
                    city: caseItem?.city || "",
                    status: caseItem?.status || "",
                    department: caseItem.department,
                    allImages: caseItem.allImages,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* UPLOAD DIALOG */}
      {opencaptureDialog && (
        <UploadMediaDialog
          open={opencaptureDialog}
          onClose={() => setOpenCaptureDialog(false)}
          userId={userId}
          data={dialogMedia || []}
          fetchCaseData={fetchCaseData}
        />
      )}

      {/* MEDIA PREVIEW DIALOG */}
      {images?.length > 0 && openDialog && (
        <MediaPreviewDialog
          open={openDialog}
          onClose={handleDialogClose}
          mediaItems={images}
          initialIndex={0}
        />
      )}
    </Box>
  );
};

export default Home;
